import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloudServerOutlined,
  DatabaseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  FileSearchOutlined,
  InfoCircleOutlined,
  MoreOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  StopOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Collapse,
  Dropdown,
  Empty,
  Grid,
  Menu,
  Modal,
  Progress,
  Skeleton,
  Space,
  Steps,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { UploadFile, UploadProps } from "antd/es/upload/interface";
import moment from "moment";

import {
  cancelStockMovementImport,
  deleteStockMovementImport,
  downloadOriginalStockMovementFile,
  downloadStockMovementImportErrors,
  getStockMovementImportProgress,
  getStockMovementImports,
  startStockMovementImport,
  uploadStockMovementFile,
} from "../../api/Services";
import { BusinessUniteContext } from "../../components/layout/Layout";
import ReusableUpload from "../../components/upload/ReusableUpload";
import styles from "./StockMovementImport.module.scss";

const { Panel } = Collapse;
const { Step } = Steps;
const { Text, Title } = Typography;

type ImportStatus =
  | "PENDING"
  | "PROCESSING"
  | "COMPLETED"
  | "ERROR"
  | "CANCELLED";

type ImportPhase =
  | "QUEUED"
  | "STARTING"
  | "READING"
  | "VALIDATING"
  | "APPLYING"
  | "FINALIZING"
  | "COMPLETED"
  | "CANCELLED"
  | "FAILED";

interface ImportRecord {
  id: string;
  originalFilename: string;
  status: ImportStatus;
  phase?: ImportPhase;
  startedAt?: string;
  finishedAt?: string;
  lastProgressAt?: string;
  totalLines: number;
  processedLines: number;
  correct: number;
  incorrect: number;
  percentCompleted: number;
  resultSummary?: string;
  userEmail: string;
  organizationName: string;
  fileKey: string;
}

const POLLING_INTERVAL = 3000;
const numberFormatter = new Intl.NumberFormat("es-UY");

const phaseSteps: Array<{
  phase: Exclude<ImportPhase, "COMPLETED" | "CANCELLED" | "FAILED">;
  label: string;
  icon: React.ReactNode;
}> = [
  {
    phase: "QUEUED",
    label: "StockImport-PhaseQueued",
    icon: <ClockCircleOutlined />,
  },
  {
    phase: "STARTING",
    label: "StockImport-PhaseStarting",
    icon: <CloudServerOutlined />,
  },
  {
    phase: "READING",
    label: "StockImport-PhaseReading",
    icon: <FileSearchOutlined />,
  },
  {
    phase: "VALIDATING",
    label: "StockImport-PhaseValidating",
    icon: <CheckCircleOutlined />,
  },
  {
    phase: "APPLYING",
    label: "StockImport-PhaseApplying",
    icon: <DatabaseOutlined />,
  },
  {
    phase: "FINALIZING",
    label: "StockImport-PhaseFinalizing",
    icon: <SyncOutlined />,
  },
];

const clampPercent = (value?: number) =>
  Math.min(100, Math.max(0, Number.isFinite(value) ? Number(value) : 0));

const resolvePhase = (record: ImportRecord): ImportPhase => {
  if (record.phase) return record.phase;
  if (record.status === "COMPLETED") return "COMPLETED";
  if (record.status === "CANCELLED") return "CANCELLED";
  if (record.status === "ERROR") return "FAILED";
  if (record.status === "PENDING") return "QUEUED";

  const percent = clampPercent(record.percentCompleted);
  if (percent < 2) return "STARTING";
  if (percent < 5) return "READING";
  if (percent < 50) return "VALIDATING";
  if (percent < 95) return "APPLYING";
  return "FINALIZING";
};

const phaseStepIndex = (record: ImportRecord) => {
  const phase = resolvePhase(record);
  const directIndex = phaseSteps.findIndex((item) => item.phase === phase);
  if (directIndex >= 0) return directIndex;
  if (phase === "COMPLETED") return phaseSteps.length;

  const percent = clampPercent(record.percentCompleted);
  if (percent < 1) return 0;
  if (percent < 2) return 1;
  if (percent < 5) return 2;
  if (percent < 50) return 3;
  if (percent < 95) return 4;
  return 5;
};

const formatDuration = (durationMs: number) => {
  if (!Number.isFinite(durationMs) || durationMs < 0) return "—";
  const totalSeconds = Math.max(0, Math.floor(durationMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return `${hours} h ${minutes} min`;
  if (minutes > 0) return `${minutes} min ${seconds} s`;
  return `${seconds} s`;
};

const elapsedFor = (record: ImportRecord, clock: number) => {
  if (!record.startedAt) return "—";
  const startedAt = Date.parse(record.startedAt);
  const finishedAt = record.finishedAt ? Date.parse(record.finishedAt) : clock;
  return formatDuration(finishedAt - startedAt);
};

const estimatedRemainingFor = (record: ImportRecord, clock: number) => {
  if (record.status !== "PROCESSING" || !record.startedAt) return "—";
  const percent = clampPercent(record.percentCompleted);
  if (percent < 3 || percent >= 100) return null;

  const elapsedMs = clock - Date.parse(record.startedAt);
  const remainingMs = (elapsedMs / percent) * (100 - percent);
  return formatDuration(remainingMs);
};

const errorMessage = (error: any, fallback: string) =>
  error?.response?.data?.apierror?.message_ES ??
  error?.response?.data?.apierror?.message ??
  error?.response?.data?.message ??
  fallback;

const StockMovementImport: React.FC = () => {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const isMobileHistory = !screens.md;
  const { contextValue: businessUnitSelected } =
    useContext(BusinessUniteContext);

  const [imports, setImports] = useState<ImportRecord[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [autoStart, setAutoStart] = useState(false);
  const [actionProcessId, setActionProcessId] = useState<string>();
  const [clock, setClock] = useState(Date.now());

  const loadImports = useCallback(
    async (showSkeleton = false) => {
      if (showSkeleton) setInitialLoading(true);
      else setRefreshing(true);

      try {
        const response = await getStockMovementImports(0);
        setImports(response.data.data.content || []);
      } catch (error) {
        message.error(errorMessage(error, t("Error-LoadingImports") as string));
      } finally {
        setInitialLoading(false);
        setRefreshing(false);
      }
    },
    [t]
  );

  useEffect(() => {
    if (businessUnitSelected) {
      void loadImports(true);
    }
  }, [businessUnitSelected, loadImports]);

  const processingIds = useMemo(
    () =>
      imports
        .filter((item) => item.status === "PROCESSING")
        .map((item) => item.id)
        .sort()
        .join(","),
    [imports]
  );

  useEffect(() => {
    const ids = processingIds ? processingIds.split(",") : [];
    if (ids.length === 0) return;

    let disposed = false;
    let polling = false;

    const poll = async () => {
      if (polling) return;
      polling = true;
      try {
        const updates = await Promise.all(
          ids.map(async (id) => {
            try {
              const response = await getStockMovementImportProgress(id);
              return response.data as ImportRecord;
            } catch (error) {
              console.error("Error polling stock import progress:", id, error);
              return null;
            }
          })
        );

        if (!disposed) {
          const updatesById = new Map(
            updates
              .filter((item): item is ImportRecord => item !== null)
              .map((item) => [item.id, item])
          );
          setImports((current) =>
            current.map((item) => updatesById.get(item.id) || item)
          );
        }
      } finally {
        polling = false;
      }
    };

    void poll();
    const interval = window.setInterval(poll, POLLING_INTERVAL);
    return () => {
      disposed = true;
      window.clearInterval(interval);
    };
  }, [processingIds]);

  useEffect(() => {
    if (!processingIds) return;
    const interval = window.setInterval(() => setClock(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [processingIds]);

  const activeImport = imports.find((item) => item.status === "PROCESSING");
  const featuredImport = activeImport || imports[0];

  const relativeActivity = (record: ImportRecord) => {
    if (!record.lastProgressAt) return t("StockImport-NoActivityYet");
    const seconds = Math.max(
      0,
      Math.floor((clock - Date.parse(record.lastProgressAt)) / 1000)
    );
    if (seconds < 5) return t("StockImport-ActivityNow");
    if (seconds < 60) {
      return t("StockImport-ActivitySeconds", { count: seconds });
    }
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return t("StockImport-ActivityMinutes", { count: minutes });
    }
    return t("StockImport-ActivityHours", {
      count: Math.floor(minutes / 60),
    });
  };

  const phaseLabel = (record: ImportRecord) => {
    const phase = resolvePhase(record);
    if (phase === "COMPLETED") return t("StockImport-PhaseCompleted");
    if (phase === "CANCELLED") return t("StockImport-PhaseCancelled");
    if (phase === "FAILED") return t("StockImport-PhaseFailed");
    const step = phaseSteps.find((item) => item.phase === phase);
    return t(step?.label || "StockImport-PhaseStarting");
  };

  const handleUpload: UploadProps["onChange"] = (info) => {
    setFileList(info.fileList);

    if (info.file.status === "done") {
      message.success(`${info.file.name} ${t("Common-UploadSuccess")}`);
      setFileList([]);
      void loadImports(false);
    }
  };

  const handleUploadRequest = async (options: any) => {
    const { file, onSuccess, onError } = options;
    setUploading(true);
    try {
      const response = await uploadStockMovementFile(file, autoStart);
      onSuccess(response.data, file);
    } catch (error: any) {
      onError(error);
      message.error(errorMessage(error, t("Error-UploadFailed") as string));
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = (processId: string) => {
    Modal.confirm({
      title: t("StockImport-CancelTitle"),
      content: t("StockImport-CancelDescription"),
      okText: t("Common-Cancel"),
      okType: "danger",
      cancelText: t("Common-Close"),
      onOk: async () => {
        setActionProcessId(processId);
        try {
          await cancelStockMovementImport(processId);
          message.success(t("Common-Cancelled"));
          await loadImports(false);
        } catch (error) {
          message.error(errorMessage(error, t("Error-CancelFailed") as string));
        } finally {
          setActionProcessId(undefined);
        }
      },
    });
  };

  const handleDelete = (processId: string) => {
    Modal.confirm({
      title: t("Common-ConfirmDelete"),
      content: t("Common-DeleteWarning"),
      okText: t("Common-Delete"),
      okType: "danger",
      cancelText: t("Common-Cancel"),
      onOk: async () => {
        setActionProcessId(processId);
        try {
          await deleteStockMovementImport(processId);
          message.success(t("Common-Deleted"));
          await loadImports(false);
        } catch (error) {
          message.error(errorMessage(error, t("Error-DeleteFailed") as string));
        } finally {
          setActionProcessId(undefined);
        }
      },
    });
  };

  const downloadFromSecureUrl = async (
    responsePromise: Promise<any>,
    fallbackError: string
  ) => {
    try {
      const response = await responsePromise;
      const downloadResponse = await fetch(response.downloadUrl);
      if (!downloadResponse.ok) {
        throw new Error(`HTTP ${downloadResponse.status}`);
      }
      const blob = await downloadResponse.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = response.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success(t("Common-DownloadStarted"));
    } catch (error) {
      console.error("Stock import download failed:", error);
      message.error(errorMessage(error, fallbackError));
    }
  };

  const handleDownloadErrors = (processId: string) =>
    downloadFromSecureUrl(
      downloadStockMovementImportErrors(processId),
      t("Error-DownloadFailed") as string
    );

  const handleDownloadOriginal = (processId: string) =>
    downloadFromSecureUrl(
      downloadOriginalStockMovementFile(processId),
      t("Error-DownloadFailed") as string
    );

  const handleStart = async (processId: string) => {
    setActionProcessId(processId);
    try {
      await startStockMovementImport(processId);
      message.success(t("Common-Started"));
      await loadImports(false);
    } catch (error) {
      message.error(errorMessage(error, t("Error-StartFailed") as string));
    } finally {
      setActionProcessId(undefined);
    }
  };

  const getStatusTag = (status: ImportStatus) => {
    switch (status) {
      case "PENDING":
        return <Tag icon={<ClockCircleOutlined />}>{t("Common-Pending")}</Tag>;
      case "PROCESSING":
        return (
          <Tag color="processing" icon={<SyncOutlined spin />}>
            {t("Common-Processing")}
          </Tag>
        );
      case "COMPLETED":
        return (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            {t("Common-Completed")}
          </Tag>
        );
      case "ERROR":
        return (
          <Tag color="error" icon={<ExclamationCircleOutlined />}>
            {t("Common-Error")}
          </Tag>
        );
      case "CANCELLED":
        return (
          <Tag color="orange" icon={<StopOutlined />}>
            {t("Common-Cancelled")}
          </Tag>
        );
    }
  };

  const renderRecordActions = (record: ImportRecord) => (
    <Space wrap>
      {record.status === "PENDING" && (
        <Button
          type="primary"
          size="small"
          icon={<PlayCircleOutlined />}
          loading={actionProcessId === record.id}
          onClick={() => void handleStart(record.id)}
        >
          {t("Common-Start")}
        </Button>
      )}
      {record.status === "PROCESSING" && (
        <Button
          danger
          size="small"
          icon={<StopOutlined />}
          loading={actionProcessId === record.id}
          onClick={() => handleCancel(record.id)}
        >
          {t("Common-Cancel")}
        </Button>
      )}
      {record.incorrect > 0 && ["COMPLETED", "ERROR"].includes(record.status) && (
        <Button
          size="small"
          icon={<DownloadOutlined />}
          onClick={() => void handleDownloadErrors(record.id)}
        >
          {t("Common-DownloadErrors")}
        </Button>
      )}
      <Button
        size="small"
        icon={<DownloadOutlined />}
        onClick={() => void handleDownloadOriginal(record.id)}
      >
        {t("StockImport-DownloadOriginal")}
      </Button>
    </Space>
  );

  const renderFeaturedCard = (record: ImportRecord) => {
    const percent = clampPercent(record.percentCompleted);
    const remaining = estimatedRemainingFor(record, clock);
    const isProcessing = record.status === "PROCESSING";
    const isTerminal = ["COMPLETED", "ERROR", "CANCELLED"].includes(
      record.status
    );

    return (
      <Card className={styles.liveCard} bordered={false} aria-live="polite">
        <div className={styles.liveHeader}>
          <div className={styles.liveIdentity}>
            <Text className={styles.eyebrow}>
              {isProcessing
                ? t("StockImport-ActiveImport")
                : t("StockImport-LatestImport")}
            </Text>
            <div className={styles.liveTitleRow}>
              <Tooltip title={record.originalFilename}>
                <Title level={4} ellipsis>
                  {record.originalFilename}
                </Title>
              </Tooltip>
              {getStatusTag(record.status)}
            </div>
            <Text type="secondary">{phaseLabel(record)}</Text>
          </div>
          <div className={styles.liveActions}>
            {renderRecordActions(record)}
          </div>
        </div>

        <div className={styles.progressHeader}>
          <Text strong>{phaseLabel(record)}</Text>
          <Text strong>{percent.toFixed(1)}%</Text>
        </div>
        <Progress
          percent={percent}
          showInfo={false}
          strokeWidth={10}
          status={
            record.status === "ERROR"
              ? "exception"
              : record.status === "COMPLETED"
              ? "success"
              : record.status === "CANCELLED"
              ? "normal"
              : "active"
          }
        />

        <div className={styles.phaseRail}>
          <Steps
            size="small"
            current={phaseStepIndex(record)}
            status={record.status === "ERROR" ? "error" : "process"}
            responsive
          >
            {phaseSteps.map((step) => (
              <Step key={step.phase} title={t(step.label)} icon={step.icon} />
            ))}
          </Steps>
        </div>

        <div className={styles.metricsGrid}>
          <div className={styles.metric}>
            <Text type="secondary">{t("StockImport-RowsRead")}</Text>
            <strong>
              {numberFormatter.format(record.processedLines || 0)}
              <span>
                {" / "}
                {record.totalLines
                  ? numberFormatter.format(record.totalLines)
                  : "—"}
              </span>
            </strong>
          </div>
          <div className={styles.metric}>
            <Text type="secondary">{t("Common-Errors")}</Text>
            <strong className={record.incorrect > 0 ? styles.errorValue : ""}>
              {numberFormatter.format(record.incorrect || 0)}
            </strong>
          </div>
          <div className={styles.metric}>
            <Text type="secondary">{t("StockImport-Elapsed")}</Text>
            <strong>{elapsedFor(record, clock)}</strong>
          </div>
          <div className={styles.metric}>
            <Text type="secondary">{t("StockImport-Remaining")}</Text>
            <strong>
              {remaining === null
                ? t("StockImport-Calculating")
                : remaining || "—"}
            </strong>
          </div>
        </div>

        {isProcessing && (
          <Alert
            className={styles.backgroundAlert}
            type="info"
            showIcon
            message={t("StockImport-BackgroundTitle")}
            description={
              <Space direction="vertical" size={2}>
                <span>{t("StockImport-BackgroundDescription")}</span>
                <span className={styles.activity}>
                  <span className={styles.activityDot} />
                  {relativeActivity(record)}
                </span>
              </Space>
            }
          />
        )}

        {isTerminal && (
          <Alert
            className={styles.backgroundAlert}
            type={
              record.status === "COMPLETED"
                ? "success"
                : record.status === "ERROR"
                ? "error"
                : "warning"
            }
            showIcon
            message={
              record.status === "COMPLETED"
                ? t("StockImport-CompletedSummary", {
                    correct: numberFormatter.format(record.correct || 0),
                    errors: numberFormatter.format(record.incorrect || 0),
                  })
                : record.status === "ERROR"
                ? t("StockImport-FailedSummary")
                : t("StockImport-CancelledSummary")
            }
          />
        )}
      </Card>
    );
  };

  const actionsMenu = (record: ImportRecord) => (
    <Menu>
      {record.status === "PENDING" && (
        <Menu.Item key="start" onClick={() => void handleStart(record.id)}>
          <PlayCircleOutlined /> {t("Common-Start")}
        </Menu.Item>
      )}
      {record.status === "PROCESSING" && (
        <Menu.Item key="cancel" onClick={() => handleCancel(record.id)}>
          <StopOutlined /> {t("Common-Cancel")}
        </Menu.Item>
      )}
      {record.incorrect > 0 && ["COMPLETED", "ERROR"].includes(record.status) && (
        <Menu.Item
          key="errors"
          onClick={() => void handleDownloadErrors(record.id)}
        >
          <DownloadOutlined /> {t("Common-DownloadErrors")}
        </Menu.Item>
      )}
      <Menu.Item
        key="original"
        onClick={() => void handleDownloadOriginal(record.id)}
      >
        <DownloadOutlined /> {t("StockImport-DownloadOriginal")}
      </Menu.Item>
      <Menu.Item
        key="delete"
        danger
        disabled={record.status === "PROCESSING"}
        onClick={() => handleDelete(record.id)}
      >
        <DeleteOutlined /> {t("Common-Delete")}
      </Menu.Item>
    </Menu>
  );

  const columns: ColumnsType<ImportRecord> = [
    {
      title: t("Common-Filename"),
      dataIndex: "originalFilename",
      key: "originalFilename",
      width: 190,
      render: (filename: string) => (
        <Tooltip title={filename} placement="topLeft">
          <Text strong ellipsis className={styles.filename}>
            {filename}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: t("Common-Status"),
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (status: ImportStatus) => getStatusTag(status),
    },
    {
      title: t("Common-Progress"),
      dataIndex: "percentCompleted",
      key: "percentCompleted",
      width: 150,
      render: (percent: number, record) => (
        <div className={styles.tableProgress}>
          <Progress
            percent={clampPercent(percent)}
            size="small"
            status={
              record.status === "ERROR"
                ? "exception"
                : record.status === "COMPLETED"
                ? "success"
                : undefined
            }
            showInfo={false}
          />
          <Text type="secondary">
            {clampPercent(percent).toFixed(1)}% · {phaseLabel(record)}
          </Text>
        </div>
      ),
    },
    {
      title: t("Common-Results"),
      key: "results",
      width: 130,
      responsive: ["lg"],
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text type="secondary">
            {t("Common-Total")}:{" "}
            {numberFormatter.format(record.totalLines || 0)}
          </Text>
          <Text type="success">
            {t("Common-Correct")}: {numberFormatter.format(record.correct || 0)}
          </Text>
          <Text type="danger">
            {t("Common-Errors")}:{" "}
            {numberFormatter.format(record.incorrect || 0)}
          </Text>
        </Space>
      ),
    },
    {
      title: t("Common-Started"),
      dataIndex: "startedAt",
      key: "startedAt",
      width: 165,
      responsive: ["xl"],
      render: (date?: string) =>
        date ? moment(date).format("DD/MM/YYYY HH:mm:ss") : "—",
    },
    {
      title: t("Common-Finished"),
      dataIndex: "finishedAt",
      key: "finishedAt",
      width: 165,
      responsive: ["xxl"],
      render: (date?: string) =>
        date ? moment(date).format("DD/MM/YYYY HH:mm:ss") : "—",
    },
    {
      title: t("Common-Actions"),
      key: "actions",
      align: "center",
      width: 72,
      render: (_, record) => (
        <Dropdown
          overlay={actionsMenu(record)}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button
            aria-label={t("Common-Actions") as string}
            icon={<MoreOutlined />}
            size="small"
            loading={actionProcessId === record.id}
          />
        </Dropdown>
      ),
    },
  ];

  const instructionDetails = (
    <div className={styles.instructions}>
      <div>{t("Common-ImportDescription")}</div>
      <ul>
        <li>{t("Common-SupportedFormats")}: CSV, ZIP</li>
        <li>
          {t("Common-RequiredColumns")}: SKU External ID, Branch Office External
          ID, Stock
        </li>
        <li>{t("Common-MaxFileSize")}: 100MB</li>
      </ul>
    </div>
  );

  return (
    <div className={styles.container}>
      <Title level={2}>{t("Common-StockMovementImport")}</Title>

      <Card className={styles.uploadCard} size="small">
        <Collapse ghost>
          <Panel
            key="instructions"
            header={
              <span>
                <InfoCircleOutlined /> {t("Common-ImportInstructions")}
              </span>
            }
          >
            {instructionDetails}
          </Panel>
        </Collapse>
        <div className={styles.uploadSection}>
          <div className={styles.fileSelection}>
            <ReusableUpload
              accept=".csv,.zip"
              disabled={uploading || Boolean(activeImport)}
              loading={uploading}
              fileList={fileList}
              onChange={handleUpload}
              onUploadRequest={handleUploadRequest}
              mode="compact"
              fullWidth={isMobileHistory}
              showUploadButton
            />
          </div>
          <Checkbox
            checked={autoStart}
            disabled={Boolean(activeImport)}
            onChange={(event) => setAutoStart(event.target.checked)}
          >
            {t("Common-AutoStart")}
          </Checkbox>
        </div>
      </Card>

      {initialLoading ? (
        <Card className={styles.liveCard} bordered={false}>
          <Skeleton active paragraph={{ rows: 4 }} />
        </Card>
      ) : (
        featuredImport && renderFeaturedCard(featuredImport)
      )}

      <Card className={styles.historyCard} size="small">
        <div className={styles.cardHeader}>
          <Title level={4}>{t("Common-ImportHistory")}</Title>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => void loadImports(false)}
            loading={refreshing}
            size="small"
          >
            {t("Common-Refresh")}
          </Button>
        </div>

        {initialLoading ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : isMobileHistory ? (
          imports.length > 0 ? (
            <div className={styles.mobileHistoryList}>
              {imports.map((record) => {
                const percent = clampPercent(record.percentCompleted);

                return (
                  <Card
                    key={record.id}
                    size="small"
                    className={styles.mobileHistoryCard}
                  >
                    <div className={styles.mobileRecordHeader}>
                      <div className={styles.mobileRecordIdentity}>
                        <Tooltip title={record.originalFilename}>
                          <Text strong ellipsis>
                            {record.originalFilename}
                          </Text>
                        </Tooltip>
                        <Text type="secondary">{phaseLabel(record)}</Text>
                      </div>
                      {getStatusTag(record.status)}
                    </div>
                    <div className={styles.mobileProgressHeader}>
                      <Text type="secondary">{t("Common-Progress")}</Text>
                      <Text strong>{percent.toFixed(1)}%</Text>
                    </div>
                    <Progress
                      percent={percent}
                      size="small"
                      showInfo={false}
                      status={
                        record.status === "ERROR"
                          ? "exception"
                          : record.status === "COMPLETED"
                          ? "success"
                          : undefined
                      }
                    />
                    <div className={styles.mobileRecordMetrics}>
                      <div>
                        <Text type="secondary">
                          {t("StockImport-RowsRead")}
                        </Text>
                        <strong>
                          {numberFormatter.format(record.processedLines || 0)} /{" "}
                          {numberFormatter.format(record.totalLines || 0)}
                        </strong>
                      </div>
                      <div>
                        <Text type="secondary">{t("Common-Errors")}</Text>
                        <strong
                          className={
                            record.incorrect > 0 ? styles.errorValue : ""
                          }
                        >
                          {numberFormatter.format(record.incorrect || 0)}
                        </strong>
                      </div>
                    </div>
                    <div className={styles.mobileRecordActions}>
                      {renderRecordActions(record)}
                      {record.status !== "PROCESSING" && (
                        <Button
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => handleDelete(record.id)}
                        >
                          {t("Common-Delete")}
                        </Button>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )
        ) : (
          <Table<ImportRecord>
            columns={columns}
            dataSource={imports}
            rowKey="id"
            size="small"
            rowClassName={(record) =>
              record.status === "PROCESSING" ? styles.processingRow : ""
            }
            expandable={{
              expandedRowRender: (record) => (
                <div className={styles.expandedDetails}>
                  <div>
                    <Text type="secondary">
                      {t("StockImport-CurrentPhase")}
                    </Text>
                    <strong>{phaseLabel(record)}</strong>
                  </div>
                  <div>
                    <Text type="secondary">{t("StockImport-RowsRead")}</Text>
                    <strong>
                      {numberFormatter.format(record.processedLines || 0)} /{" "}
                      {numberFormatter.format(record.totalLines || 0)}
                    </strong>
                  </div>
                  <div>
                    <Text type="secondary">{t("StockImport-Elapsed")}</Text>
                    <strong>{elapsedFor(record, clock)}</strong>
                  </div>
                  <div>
                    <Text type="secondary">
                      {t("StockImport-LastActivity")}
                    </Text>
                    <strong>{relativeActivity(record)}</strong>
                  </div>
                </div>
              ),
            }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              responsive: true,
              showTotal: (total, range) =>
                t("Common-PaginationInfo", {
                  from: range[0],
                  to: range[1],
                  total,
                }),
              locale: {
                items_per_page: t("Common-ItemsPerPage"),
              },
            }}
          />
        )}
      </Card>
    </div>
  );
};

export default StockMovementImport;
